import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, KeyRound, CheckCircle2, AlertTriangle, ShieldCheck } from "lucide-react";
import Logo from "../components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SetPassword() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null); // {name,email}
  const [error, setError] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const check = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const res = await fetch(`${API}/auth/set-password/${token}`);
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || "This link is invalid or has expired.");
      setInfo(d);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [token]);
  useEffect(() => { check(); }, [check]);

  const submit = async () => {
    if (pw.length < 6) { setError("Password must be at least 6 characters."); return; }
    if (pw !== pw2) { setError("The two passwords don't match."); return; }
    setBusy(true); setError("");
    try {
      const res = await fetch(`${API}/auth/set-password`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password: pw }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || "Could not set your password.");
      setDone(true);
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="min-h-screen bg-slate-100 flex items-center justify-center" data-testid="setpw-loading"><Loader2 className="h-6 w-6 animate-spin text-sky-500" /></div>;

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4" data-testid="set-password-page">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-5"><Logo /></div>
        <div className="bg-white rounded-2xl shadow-sm p-7">
          {error && !info ? (
            <div className="text-center" data-testid="setpw-invalid">
              <AlertTriangle className="h-8 w-8 text-amber-500 mx-auto mb-3" />
              <p className="font-display font-bold text-slate-900 text-lg">Link unavailable</p>
              <p className="text-sm text-slate-500 mt-2">{error}</p>
            </div>
          ) : done ? (
            <div className="text-center" data-testid="setpw-done">
              <CheckCircle2 className="h-9 w-9 text-emerald-600 mx-auto mb-3" />
              <p className="font-display font-bold text-slate-900 text-lg">Password set!</p>
              <p className="text-sm text-slate-500 mt-2">You can now sign in with your email and new password.</p>
              <Link to="/software" data-testid="setpw-signin-link" className="mt-5 inline-flex items-center justify-center gap-2 rounded-full bg-slate-900 hover:bg-sky-700 text-white text-sm font-semibold px-6 py-3 transition-colors">Go to sign in</Link>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 text-sky-600 text-xs font-mono2 uppercase tracking-widest mb-2"><KeyRound className="h-4 w-4" /> Set your password</div>
              <p className="font-display font-bold text-slate-900 text-lg">Welcome{info?.name ? `, ${info.name}` : ""}</p>
              <p className="text-sm text-slate-500 mt-1">Choose a password for <span className="font-semibold text-slate-700">{info?.email}</span> to access your customer portal.</p>

              <label className="text-[10px] font-mono2 uppercase tracking-widest text-slate-400 block mt-5">New password</label>
              <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="setpw-password" placeholder="At least 6 characters" className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-sky-400" />
              <label className="text-[10px] font-mono2 uppercase tracking-widest text-slate-400 block mt-3">Confirm password</label>
              <input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="setpw-confirm" placeholder="Re-type your password" className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-sky-400" />

              {error && <p className="text-sm text-rose-500 mt-3" data-testid="setpw-error">{error}</p>}

              <button onClick={submit} disabled={busy} data-testid="setpw-submit-btn" className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-slate-900 hover:bg-sky-700 disabled:opacity-60 text-white text-sm font-semibold py-3 transition-colors">
                {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving…</> : "Set password"}
              </button>
              <p className="text-center text-[11px] text-slate-400 mt-3 inline-flex items-center gap-1.5 w-full justify-center"><ShieldCheck className="h-3.5 w-3.5" /> This is a one-time link and expires for your security.</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
